import { useState, KeyboardEvent, FormEvent, useRef } from 'react';
import { Box, TextField, Button, useTheme, Typography, Autocomplete } from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import ChatPageRadioButtons from './ChatPageRadioButtons';
import { SearchTopic, SearchTopics } from '../../enum/SearchTopics';

interface ChatInputProps {
  onSendMessage: (message: string, current_turn: number) => void;
  selectedOption: SearchTopic;
  onSelectedOption: (option: SearchTopic) => void;
  disabled: boolean;
}

const suggestedMaterialQuestions: string[] = [
  'What materials are fire resistant?',
  'What materials are flame resistant and for use on wires?',
  'find me a list of materials provided in self lam format',
  'what materials are for high temperature identification of wires',
  'what materials meet the requirements of AS23053 / 5',
  "I'm looking for a lab material that can be placed on frozen vials.",
  'What printers are compatible with {B_Number}?',
  'I need a vinyl label in red that last 30 years',
  'What is the temp rating of B-593?',
  'which materials have been tested to MIL-STD-202',
  'is B361B solvent resistant',
  'Do you have an overlaminate?',
  'looking for a material that sticks to stainless steel',
  'show me materials that have a high abrasion resitance',
  'heat shrink material in the i3300 printer',
  'white esd for M610 printer',
  'Black on white vinyl compatible with the M210',
  'labels for lab',
  'What materials are compatible with R6000',
];

const suggestedProductQuestions: string[] = [
  'What signs are plastic and are 10x14',
  'what labels are made from B-724 and are used on circuit boards',
  'Show me Thermal printers with 300 dpi',
  'reflective acrylic tape',
  'circuit breaker lockout',
  'Do we have any blue braille signs',
  'show me some permanent sewer pipe markers',
  'show me products that can absorb 40 gallons of liquid',
  'show me signs that say danger',
];

const ChatInput: React.FC<ChatInputProps> = ({ onSendMessage, selectedOption, onSelectedOption, disabled }) => {
  const theme = useTheme();
  const [message, setMessage] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const isInputDisabled = selectedOption === SearchTopics.None;

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (message.trim() && !isInputDisabled) {
      onSendMessage(message, 1);
      setMessage('');
    }
  };

  const handleKeyPress = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' && !e.shiftKey && !isInputDisabled && !disabled) {
      e.preventDefault();
      if (message.trim()) {
        onSendMessage(message, 1);
        setMessage('');
        if (inputRef.current) {
          // Remove focus to close autocomplete options
          inputRef.current.blur();
        }
      }
    }
  };

  const handleButtonClick = () => {
    if (message.trim() && !isInputDisabled) {
      onSendMessage(message, 1);
      setMessage('');
    }
  };

  const getPromptText = () => {
    switch (selectedOption) {
      case SearchTopics.MaterialSearch:
        return 'Search for a material';
      case SearchTopics.ProductSearch:
        return 'Search for a product';
      case SearchTopics.None:
      default:
        return 'Please select an option to continue';
    }
  };

  return (
    <Box
      component="form"
      onSubmit={handleSubmit}
      sx={{
        // position: 'fixed',
        bottom: 16,
        left: 76,
        right: 640,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
        boxShadow: 3,
        borderRadius: 2,
        p: 1,
        background: '#ffffff',
        width: '100%',
      }}
    >
      <Typography variant="h4" component="h1" gutterBottom>
        What are you looking for?
      </Typography>
      <ChatPageRadioButtons selectedOption={selectedOption} setSelectedOption={onSelectedOption} />
      <Typography variant="body2" sx={{ mb: 1, color: theme.palette.text.secondary }}>
        {getPromptText()}
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <Autocomplete
          fullWidth
          freeSolo
          value={message}
          onInputChange={(_event, newInputValue) => {
            setMessage(newInputValue);
          }}
          options={
            selectedOption === SearchTopics.MaterialSearch ? suggestedMaterialQuestions : suggestedProductQuestions
          }
          renderInput={(params) => (
            <TextField
              {...params}
              inputRef={inputRef}
              multiline
              minRows={1}
              maxRows={10}
              onKeyUp={handleKeyPress}
              placeholder={isInputDisabled ? 'Select an option to enable input' : 'Type your message here...'}
              variant="outlined"
            />
          )}
          sx={{ mr: 1 }}
          disabled={isInputDisabled}
        />
        <Button
          type="submit"
          variant="contained"
          color="primary"
          endIcon={<SendIcon />}
          onClick={handleButtonClick}
          disabled={isInputDisabled || disabled}
        >
          Send
        </Button>
      </Box>
    </Box>
  );
};

export default ChatInput;
